import * as THREE from 'three';
import { Noise } from './Noise.js';
import { ROCKS } from '../utils/constants.js';
import { SeededRandom } from '../utils/random.js';
import { pullMaterialTowardHue } from '../utils/palette.js';
import { SurfacePlan } from './SurfacePlan.js';

/* ── per-planet-type rock style ───────────────────────────────── */

const ROCK_STYLES = {
  rocky: {
    exterior: [0.07, 0.12, 0.40],     // warm grey-brown
    interior: [0.06, 0.22, 0.18],
    vein:     [0.0,  0.05, 0.72],      // silver-metallic
    roughness: 0.92, metalness: 0.05,
    noiseType: 'fbm', noiseFreq: 2.5, noiseAmp: 0.25,
    veinThreshold: 0.68,
  },
  ice: {
    exterior: [0.58, 0.22, 0.72],      // pale blue-white
    interior: [0.60, 0.40, 0.30],
    vein:     [0.52, 0.60, 0.65],      // cyan
    roughness: 0.15, metalness: 0.02,
    noiseType: 'ridge', noiseFreq: 2.0, noiseAmp: 0.18,
    veinThreshold: 0.72,
  },
  lava: {
    exterior: [0.0,  0.06, 0.13],      // near-black obsidian
    interior: [0.06, 0.90, 0.35],      // molten orange
    vein:     [0.03, 0.95, 0.50],      // bright orange-red
    roughness: 0.65, metalness: 0.12,
    emissive: [0.05, 0.80, 0.15],
    emissiveIntensity: 0.10,
    noiseType: 'ridge', noiseFreq: 3.0, noiseAmp: 0.30,
    veinThreshold: 0.58,
  },
  crystal: {
    exterior: [0.76, 0.55, 0.50],      // purple
    interior: [0.85, 0.70, 0.55],      // bright pink
    vein:     [0.12, 0.80, 0.60],      // golden
    roughness: 0.12, metalness: 0.35,
    emissive: [0.76, 0.50, 0.25],
    emissiveIntensity: 0.18,
    noiseType: 'ridge', noiseFreq: 4.0, noiseAmp: 0.35,
    veinThreshold: 0.60,
  },
  forest: {
    exterior: [0.30, 0.22, 0.35],      // grey-green
    interior: [0.08, 0.28, 0.15],      // dark earth
    vein:     [0.25, 0.50, 0.42],      // copper-green
    roughness: 0.90, metalness: 0.02,
    noiseType: 'warp', noiseFreq: 2.0, noiseAmp: 0.22,
    veinThreshold: 0.70,
  },
  desert: {
    exterior: [0.08, 0.40, 0.55],      // tan / sand
    interior: [0.04, 0.45, 0.25],      // red-brown
    vein:     [0.07, 0.70, 0.52],      // copper
    roughness: 0.85, metalness: 0.08,
    noiseType: 'fbm', noiseFreq: 3.0, noiseAmp: 0.20,
    veinThreshold: 0.65,
  },
  ocean: {
    exterior: [0.48, 0.25, 0.40],      // teal-grey
    interior: [0.50, 0.30, 0.20],      // dark teal
    vein:     [0.08, 0.15, 0.82],      // pearl white
    roughness: 0.55, metalness: 0.03,
    noiseType: 'warp', noiseFreq: 2.5, noiseAmp: 0.20,
    veinThreshold: 0.72,
  },
};

/**
 * Generates and places dense rock formations on a planet surface.
 *
 * Each rock is a high-subdivision icosahedron (10 k+ verts) deformed with
 * noise and coloured per-planet-type, giving far more vertex density than
 * the planet terrain itself.
 */
export class RockFormation {
  constructor() {
    /** @type {{ mesh: THREE.Mesh, center: THREE.Vector3, radius: number, health: number, maxHealth: number, baseEmissiveIntensity: number }[]} */
    this.rocks = [];
    this.group = new THREE.Group();
    this.group.name = 'rock-formations';
  }

  /* ── public API ────────────────────────────────── */

  /**
   * Place rock outcrops over the planet.
   *
   * Rocks come in clusters ("outcrops") whose footprints are reserved in the
   * shared SurfacePlan, so they never collide with ruins, trees, or each
   * other — the surface reads as distinct rock gardens with open ground
   * between them instead of uniform rubble.
   *
   * @param {object}      planetData   – planet config (seed, type, size, terrain …)
   * @param {number}      planetRadius – actual surface radius (SURFACE_RADIUS × size)
   * @param {function}    getHeightFn  – (normalizedDir: Vector3) => surfaceHeight
   * @param {SurfacePlan} [plan]       – shared surface zoning plan
   * @returns {{ group: THREE.Group, rocks: object[] }}
   */
  generateRocks(planetData, planetRadius, getHeightFn, plan = null) {
    this.clear();

    // No rocks on gas giants
    if (planetData.type === 'gas') {
      return { group: this.group, rocks: this.rocks };
    }

    plan = plan || new SurfacePlan(planetData, planetRadius);
    const rng = new SeededRandom(planetData.seed + 9999);

    const clusterCount = ROCKS.CLUSTERS_MIN +
      Math.floor(rng.next() * (ROCKS.CLUSTERS_MAX - ROCKS.CLUSTERS_MIN + 1));
    const anchors  = plan.pickSpreadDirs(clusterCount, ROCKS.CLUSTER_FOOTPRINT, 'rocks', rng);
    const minAngle = ROCKS.MIN_SPACING / planetRadius;

    let placed = 0;
    for (const anchor of anchors) {
      const perCluster = ROCKS.PER_CLUSTER_MIN +
        Math.floor(rng.next() * (ROCKS.PER_CLUSTER_MAX - ROCKS.PER_CLUSTER_MIN + 1));
      const clusterDirs = [];

      let clusterPlaced = 0;
      let attempts = 0;
      while (clusterPlaced < perCluster && attempts < perCluster * 12) {
        attempts++;
        const dir = plan.sampleNear(anchor, ROCKS.CLUSTER_FOOTPRINT * 0.75, rng);

        /* keep rocks within the outcrop from intersecting each other */
        let tooClose = false;
        for (const d of clusterDirs) {
          if (dir.angleTo(d) < minAngle) { tooClose = true; break; }
        }
        if (tooClose) continue;
        clusterDirs.push(dir.clone());

        /* surface contact point */
        const surfaceHeight = getHeightFn(dir);
        const surfacePoint  = dir.clone().multiplyScalar(surfaceHeight);

        /* rock parameters */
        const rockRadius = ROCKS.MIN_RADIUS + rng.next() * (ROCKS.MAX_RADIUS - ROCKS.MIN_RADIUS);
        const rockSeed   = planetData.seed + placed * 131 + 42;

        /* build mesh */
        const mesh = this._createRockMesh(rockRadius, rockSeed, planetData.type);

        /* position: embed bottom fraction into terrain */
        const embedOffset = rockRadius * ROCKS.EMBED_FRACTION;
        mesh.position.copy(surfacePoint).addScaledVector(dir, rockRadius - embedOffset);

        /* slight random rotation for variety */
        mesh.rotation.set(
          (rng.next() - 0.5) * 0.6,
          rng.next() * Math.PI * 2,
          (rng.next() - 0.5) * 0.6,
        );

        mesh.name = 'outcrop-rock';
        this.group.add(mesh);

        const health = Math.round(ROCKS.BASE_HEALTH * (rockRadius / ROCKS.MIN_RADIUS));
        this.rocks.push({
          mesh,
          center: mesh.position.clone(),
          radius: rockRadius,
          health,
          maxHealth: health,
          baseEmissiveIntensity: mesh.material.emissiveIntensity || 0,
        });

        placed++;
        clusterPlaced++;
      }
    }

    // Share the planet's palette (mirrors terrain + scatter hue shift)
    if (planetData.visual?.hue != null) {
      const visited = new Set();
      for (const rock of this.rocks) {
        pullMaterialTowardHue(rock.mesh.material, planetData.visual.hue, visited);
      }
    }

    return { group: this.group, rocks: this.rocks };
  }

  clear() {
    for (const rock of this.rocks) {
      if (rock.mesh.parent) rock.mesh.parent.remove(rock.mesh);
      rock.mesh.geometry.dispose();
      rock.mesh.material.dispose();
    }
    this.rocks = [];
    while (this.group.children.length) {
      this.group.remove(this.group.children[0]);
    }
  }

  /* ── rock mesh creation ────────────────────────── */

  _createRockMesh(radius, seed, planetType) {
    const style = ROCK_STYLES[planetType] || ROCK_STYLES.rocky;
    const rockNoise = new Noise(seed);

    /* ── geometry: dense icosahedron ── */
    const geo       = new THREE.IcosahedronGeometry(radius, ROCKS.SUBDIVISION);
    const positions = geo.attributes.position;
    const vertex    = new THREE.Vector3();

    /* ── shape deformation ── */
    for (let i = 0, n = positions.count; i < n; i++) {
      vertex.set(positions.getX(i), positions.getY(i), positions.getZ(i));
      const dir = vertex.clone().normalize();
      const r   = vertex.length();

      const nx = dir.x * style.noiseFreq;
      const ny = dir.y * style.noiseFreq;
      const nz = dir.z * style.noiseFreq;

      let deform;
      switch (style.noiseType) {
        case 'ridge':
          deform = rockNoise.ridge(nx, ny, nz, 4, 2.0, 0.5);
          break;
        case 'warp':
          deform = rockNoise.warp(nx, ny, nz, 4, 2.0, 0.5, 0.8);
          break;
        default: // 'fbm'
          deform = rockNoise.fbm(nx, ny, nz, 5, 2.0, 0.5);
      }

      // fine detail layer
      deform += rockNoise.noise3D(nx * 3, ny * 3, nz * 3) * 0.15;

      const newR = r * (1 + deform * style.noiseAmp);
      vertex.copy(dir).multiplyScalar(Math.max(r * 0.3, newR));
      positions.setXYZ(i, vertex.x, vertex.y, vertex.z);
    }

    /* ── vertex colours ── */
    const colors = new Float32Array(positions.count * 3);
    const tmpC   = new THREE.Color();
    const veinC  = new THREE.Color().setHSL(...style.vein);

    for (let i = 0, n = positions.count; i < n; i++) {
      vertex.set(positions.getX(i), positions.getY(i), positions.getZ(i));
      const dir = vertex.clone().normalize();

      // base colour with noise variation
      const cn = rockNoise.noise3D(dir.x * 4, dir.y * 4, dir.z * 4);
      tmpC.setHSL(
        style.exterior[0] + cn * 0.04,
        Math.max(0, Math.min(1, style.exterior[1] + cn * 0.08)),
        Math.max(0, Math.min(1, style.exterior[2] + cn * 0.07)),
      );

      // ore-vein pattern
      const vn = Math.abs(rockNoise.noise3D(dir.x * 8, dir.y * 8, dir.z * 8));
      if (vn > style.veinThreshold) {
        const strength = (vn - style.veinThreshold) / (1 - style.veinThreshold);
        tmpC.lerp(veinC, strength * 0.55);
      }

      colors[i * 3]     = tmpC.r;
      colors[i * 3 + 1] = tmpC.g;
      colors[i * 3 + 2] = tmpC.b;
    }

    geo.setAttribute('color', new THREE.BufferAttribute(colors, 3));
    geo.computeVertexNormals();

    /* ── material ── */
    const mat = new THREE.MeshStandardMaterial({
      vertexColors: true,
      roughness:    style.roughness,
      metalness:    style.metalness,
      flatShading:  false,
      side:         THREE.DoubleSide,  // see inside mined holes
    });

    if (style.emissive) {
      mat.emissive = new THREE.Color().setHSL(...style.emissive);
      mat.emissiveIntensity = style.emissiveIntensity || 0;
    } else {
      mat.emissive = new THREE.Color(0x222222);
      mat.emissiveIntensity = 0;
    }

    return new THREE.Mesh(geo, mat);
  }
}
